import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Tabs, Tab, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, LinearProgress,
  Button, Select, MenuItem, TextField, FormControl, InputLabel,
  Alert, IconButton, Tooltip, CircularProgress, Dialog, InputAdornment,
  DialogTitle, DialogContent, DialogActions, Badge
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Warehouse as WarehouseIcon,
  SwapHoriz as TransferIcon,
  NotificationsActive as AlertIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  LocalShipping as ShippingIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ShoppingCart as OrderIcon,
  FileDownload as DownloadIcon,
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const APP_API_KEY = process.env.REACT_APP_API_KEY || 'e4ccbc0bd680211f4e4553ea3ab07d8a55707e79cde61faa10c51c55c6da35a9';
const API = '/api/inventory';

function useApi(endpoint, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}${endpoint}`, { headers: { 'x-api-key': APP_API_KEY } });
      const json = await res.json();
      if (!res.ok) { setError(json.error || `HTTP ${res.status}`); setData(null); }
      else { setData(json); setError(null); }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => { fetch_(); }, [fetch_, ...deps]);
  return { data, loading, error, refetch: fetch_ };
}

// ─── Tab 1: Overzicht ───────────────────────────────────────────────────────
function OverzichtTab({ warehouses, products, onTransferClick, onProductsRefetch }) {
  const { data: stock, loading, refetch } = useApi('/stock');
  const { data: innostock, loading: innLoading, refetch: refetchInn } = useApi('/innostock');
  const [editDialog, setEditDialog] = useState(null);
  const [editQty, setEditQty] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [togglingHidden, setTogglingHidden] = useState({});
  const [searchText, setSearchText] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  if (loading || innLoading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  // Bouw matrix: product → warehouse → qty
  const matrix = {};
  (stock || []).forEach(s => {
    const sku = s.inventory_products?.sku;
    const wh = s.inventory_warehouses?.name;
    if (!matrix[sku]) matrix[sku] = {};
    matrix[sku][wh] = s.quantity;
  });

  const innostockMap = {};
  (innostock || []).forEach(p => { innostockMap[p.sku] = p; });

  const externalWarehouses = (warehouses || []).filter(w => w.name !== 'InnoStock');
  const allSkusRaw = [...new Set([
    ...(products || []).map(p => p.sku),
    ...Object.keys(innostockMap)
  ])].sort();

  // Filter verborgen producten tenzij showHidden aan staat
  const allSkusFiltered = showHidden
    ? allSkusRaw
    : allSkusRaw.filter(sku => {
        const p = (products || []).find(x => x.sku === sku);
        return !p?.hidden;
      });

  // Filter op zoekterm (naam of SKU)
  const allSkus = searchText.trim()
    ? allSkusFiltered.filter(sku => {
        const p = (products || []).find(x => x.sku === sku);
        const name = (p?.name || innostockMap[sku]?.name || sku).toLowerCase();
        const q = searchText.toLowerCase();
        return sku.toLowerCase().includes(q) || name.includes(q);
      })
    : allSkusFiltered;

  const hiddenCount = allSkusRaw.filter(sku => {
    const p = (products || []).find(x => x.sku === sku);
    return p?.hidden;
  }).length;

  const getProductName = (sku) => {
    const p = (products || []).find(x => x.sku === sku);
    return p?.name || innostockMap[sku]?.name || sku;
  };

  const getMinLevel = (sku) => {
    const p = (products || []).find(x => x.sku === sku);
    return p?.min_level_innostock ?? 0;
  };

  const handleToggleHidden = async (sku) => {
    const product = (products || []).find(p => p.sku === sku);
    if (!product) return;
    setTogglingHidden(prev => ({ ...prev, [sku]: true }));
    try {
      await fetch(`${API}/products/${product.id}/hidden`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
        body: JSON.stringify({ hidden: !product.hidden })
      });
      if (onProductsRefetch) onProductsRefetch();
    } finally {
      setTogglingHidden(prev => ({ ...prev, [sku]: false }));
    }
  };

  const handleSaveStock = async () => {
    if (!editDialog) return;
    setSaving(true);
    setSaveError('');
    try {
      const res = await fetch(`${API}/stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
        body: JSON.stringify({
          product_id: editDialog.product_id,
          sku: editDialog.sku,
          warehouse_id: editDialog.warehouse_id,
          quantity: parseInt(editQty) || 0
        })
      });
      if (!res.ok) {
        let msg = `Opslaan mislukt (${res.status})`;
        try { const body = await res.json(); if (body?.error) msg = body.error; } catch (e) {}
        setSaveError(msg);
        return;
      }
      setEditDialog(null);
      refetch();
      if (onProductsRefetch) onProductsRefetch();
    } catch (e) {
      setSaveError(e.message || 'Onbekende fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight="bold">Voorraad per magazijn</Typography>
          <TextField
            size="small"
            placeholder="Zoek product of SKU..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            sx={{ width: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchText('')}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {hiddenCount > 0 && (
            <Button
              size="small"
              startIcon={showHidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={() => setShowHidden(v => !v)}
              variant="outlined"
              color={showHidden ? 'warning' : 'inherit'}
              sx={{ fontSize: 12 }}
            >
              {showHidden ? `Verberg ${hiddenCount} verborgen` : `${hiddenCount} verborgen`}
            </Button>
          )}
          <Button startIcon={<RefreshIcon />} onClick={() => { refetch(); refetchInn(); }} size="small">
            Vernieuwen
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 220, backgroundColor: '#f5f5f5' }}>Product</TableCell>
              {externalWarehouses.map(w => (
                <TableCell key={w.id} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  {w.name}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#fff8e1' }}>
                InnoStock
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Min.</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9' }}>Totaal</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 48 }}></TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 40 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allSkus.map(sku => {
              const inn = innostockMap[sku];
              const innQty = inn?.freestock ?? '-';
              const minLevel = getMinLevel(sku);
              const isLow = typeof innQty === 'number' && innQty < minLevel;
              const product = (products || []).find(p => p.sku === sku);

              return (
                <TableRow key={sku} hover sx={isLow ? { backgroundColor: '#fff3f3' } : {}}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {getProductName(sku)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{sku}</Typography>
                  </TableCell>
                  {externalWarehouses.map(w => {
                    const qty = matrix[sku]?.[w.name] ?? 0;
                    const stockEntry = (stock || []).find(s =>
                      s.inventory_products?.sku === sku &&
                      s.inventory_warehouses?.name === w.name
                    );
                    return (
                      <TableCell key={w.id} align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Typography variant="body2">{qty}</Typography>
                          <Tooltip title="Aanpassen">
                            <IconButton size="small" onClick={() => {
                              setEditDialog({ product_id: product?.id, warehouse_id: w.id, sku, wh: w.name });
                              setEditQty(String(qty));
                            }}>
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    );
                  })}
                  <TableCell align="center" sx={{ backgroundColor: '#fffde7' }}>
                    {isLow ? (
                      <Chip label={innQty} color="error" size="small" />
                    ) : (
                      <Typography variant="body2" fontWeight="medium">{innQty}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" color="text.secondary">{minLevel}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: '#f1f8f1' }}>
                    {(() => {
                      const extTotal = externalWarehouses.reduce((sum, w) => sum + (matrix[sku]?.[w.name] ?? 0), 0);
                      const innTotal = typeof innQty === 'number' ? innQty : 0;
                      const total = extTotal + innTotal;
                      return (
                        <Typography variant="body2" fontWeight="bold" color={total < minLevel ? 'error' : 'inherit'}>
                          {total}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center" sx={{ p: 0.5 }}>
                    <Tooltip title="Transfer boeken">
                      <IconButton size="small" color="primary" onClick={() => onTransferClick && onTransferClick(product)}>
                        <TransferIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ p: 0.5 }}>
                    <Tooltip title={product?.hidden ? 'Toon in overzicht' : 'Verberg uit overzicht'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleHidden(sku)}
                          disabled={!!togglingHidden[sku] || !product}
                          sx={{ color: product?.hidden ? 'text.disabled' : 'text.secondary', opacity: product?.hidden ? 0.4 : 1 }}
                        >
                          {product?.hidden ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit dialog */}
      <Dialog open={!!editDialog} onClose={() => { setEditDialog(null); setSaveError(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>Voorraad aanpassen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {editDialog?.sku} @ {editDialog?.wh}
          </Typography>
          <TextField
            label="Aantal" type="number" value={editQty}
            onChange={e => setEditQty(e.target.value)}
            fullWidth autoFocus inputProps={{ min: 0 }}
          />
          {saveError && (
            <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialog(null); setSaveError(''); }}>Annuleren</Button>
          <Button onClick={handleSaveStock} variant="contained" disabled={saving}>
            {saving ? 'Opslaan…' : 'Opslaan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Barcode Scanner Component (ZXing - werkt in alle browsers) ──────────────
function loadZxing() {
  return new Promise((resolve, reject) => {
    if (window.ZXing) { resolve(window.ZXing); return; }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/@zxing/library@0.19.1/umd/index.min.js';
    script.onload = () => resolve(window.ZXing);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function BarcodeScanner({ onDetected, onClose }) {
  const videoRef = React.useRef(null);
  const readerRef = React.useRef(null);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    let mounted = true;

    const start = async () => {
      try {
        const ZXing = await loadZxing();
        if (!mounted) return;

        const hints = new Map();
        const formats = [
          ZXing.BarcodeFormat.EAN_13,
          ZXing.BarcodeFormat.EAN_8,
          ZXing.BarcodeFormat.CODE_128,
          ZXing.BarcodeFormat.CODE_39,
        ];
        hints.set(ZXing.DecodeHintType.POSSIBLE_FORMATS, formats);

        const reader = new ZXing.BrowserMultiFormatReader(hints);
        readerRef.current = reader;

        if (!mounted) return;
        setScanning(true);

        // undefined = ZXing kiest zelf camera (werkt op iOS Safari/Chrome)
        await reader.decodeFromVideoDevice(undefined, videoRef.current, (result, err) => {
          if (result && mounted) {
            onDetected(result.getText());
          }
        });
      } catch (e) {
        console.error('BarcodeScanner fout:', e.name, e.message);
        if (!mounted) return;
        if (e.name === 'NotAllowedError') {
          setError('Camera-toegang geweigerd. Tik op het camera-icoon in de adresbalk en sta toe.');
        } else if (e.name === 'NotFoundError') {
          setError('Geen camera gevonden op dit apparaat.');
        } else if (e.name === 'NotReadableError') {
          setError('Camera is al in gebruik door een andere app.');
        } else {
          setError(`Camera fout: ${e.message || e.name}`);
        }
      }
    };

    start();
    return () => {
      mounted = false;
      if (readerRef.current) {
        try { readerRef.current.reset(); } catch(e) {}
      }
    };
  }, [onDetected]);

  return (
    <Box sx={{ position: 'relative' }}>
      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <Box sx={{ position: 'relative', mb: 2 }}>
          <video
            ref={videoRef}
            style={{ width: '100%', maxHeight: 300, borderRadius: 8, background: '#000', display: 'block' }}
            playsInline
            muted
          />
          {scanning && (
            <Box sx={{
              position: 'absolute', top: '50%', left: '10%', right: '10%',
              height: 2, bgcolor: '#ffcc00', opacity: 0.85,
              boxShadow: '0 0 10px #ffcc00',
              transform: 'translateY(-50%)'
            }} />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            {scanning ? 'Richt de camera op een barcode' : 'Camera laden...'}
          </Typography>
        </Box>
      )}
      <Button variant="outlined" fullWidth onClick={onClose}>Annuleren</Button>
    </Box>
  );
}

// ─── Tab 2: Transfer ────────────────────────────────────────────────────────
function TransferTab({ warehouses, products, preselectedProduct }) {
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    if (preselectedProduct) setSelectedProduct(preselectedProduct);
  }, [preselectedProduct]);
  const [fromWh, setFromWh] = useState('');
  const [toWh, setToWh] = useState('');
  const [qty, setQty] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [scanMode, setScanMode] = useState(false);

  const handleBarcode = useCallback((code) => {
    setScanMode(false);

    // Extraheer slug als het een URL is (bijv. QR op HipHot dispenser)
    let searchCode = code;
    try {
      if (code.startsWith('http')) {
        const url = new URL(code);
        const slug = url.pathname.replace(/\//g, '').trim();
        if (slug) searchCode = slug;
      }
    } catch(e) {}

    const normalize = s => (s || '').toLowerCase().replace(/[-_ ]/g, '');
    const norm = normalize(searchCode);

    const found = (products || []).find(p =>
      p.barcode === code ||
      p.sku === code ||
      normalize(p.sku) === norm ||
      normalize(p.name) === norm ||
      normalize(p.name).includes(norm) ||
      norm.includes(normalize(p.sku))
    );

    if (found) {
      setSelectedProduct(found);
    } else {
      setError(`Barcode "${searchCode}" niet gevonden. Zoek handmatig.`);
    }
  }, [products]);

  const handleSubmit = async () => {
    if (!selectedProduct || !qty || qty <= 0) {
      setError('Kies een product en vul een geldig aantal in.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/transfers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
        body: JSON.stringify({
          product_id: parseInt(selectedProduct.id),
          from_warehouse_id: fromWh ? parseInt(fromWh) : null,
          to_warehouse_id: toWh ? parseInt(toWh) : null,
          quantity: parseInt(qty),
          notes
        })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Fout bij opslaan');
      }
      setSuccess(true);
      setSelectedProduct(null); setFromWh(''); setToWh(''); setQty(''); setNotes('');
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 480 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>Transfer boeken</Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }}>Transfer opgeslagen!</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {scanMode ? (
        <BarcodeScanner onDetected={handleBarcode} onClose={() => setScanMode(false)} />
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Autocomplete
              options={products || []}
              value={selectedProduct}
              onChange={(e, val) => setSelectedProduct(val)}
              getOptionLabel={p => p ? `${p.name} (${p.sku})` : ''}
              filterOptions={(opts, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return opts.filter(p =>
                  p.name?.toLowerCase().includes(q) ||
                  p.sku?.toLowerCase().includes(q)
                );
              }}
              renderOption={(props, p) => (
                <li {...props} key={p.id}>
                  <Box>
                    <Typography variant="body2">{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.sku}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Zoek product (naam of artikelcode)" fullWidth />
              )}
              sx={{ flex: 1 }}
              noOptionsText="Geen producten gevonden"
            />
            <Tooltip title="Scan barcode">
              <Button
                variant="outlined"
                onClick={() => { setError(''); setScanMode(true); }}
                sx={{ minWidth: 48, px: 1.5, borderColor: '#ffcc00', color: '#000' }}
              >
                📷
              </Button>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Van</InputLabel>
              <Select value={fromWh} onChange={e => setFromWh(e.target.value)} label="Van">
                <MenuItem value=""><em>— Onbekend / Leverancier</em></MenuItem>
                {(warehouses || []).map(w => (
                  <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Naar</InputLabel>
              <Select value={toWh} onChange={e => setToWh(e.target.value)} label="Naar">
                <MenuItem value=""><em>— Onbekend / Klant</em></MenuItem>
                {(warehouses || []).map(w => (
                  <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Aantal" type="number" value={qty}
            onChange={e => setQty(e.target.value)}
            fullWidth sx={{ mb: 2 }} inputProps={{ min: 1 }}
          />

          <TextField
            label="Opmerking (optioneel)" value={notes}
            onChange={e => setNotes(e.target.value)}
            fullWidth multiline rows={2} sx={{ mb: 3 }}
          />

          <Button
            variant="contained" size="large" fullWidth
            onClick={handleSubmit} disabled={saving}
            sx={{ backgroundColor: '#ffcc00', color: '#000', '&:hover': { backgroundColor: '#e6b800' } }}
            startIcon={saving ? <CircularProgress size={18} /> : <TransferIcon />}
          >
            {saving ? 'Opslaan...' : 'Transfer boeken'}
          </Button>
        </>
      )}
    </Box>
  );
}

// ─── Tab 3: InnoStock Alerts ─────────────────────────────────────────────────
function AlertsTab({ products, onProductsRefetch }) {
  const { data: innostock, loading, refetch } = useApi('/innostock');
  const [editMin, setEditMin] = useState({});
  const [editLeadtime, setEditLeadtime] = useState({});
  const [saving, setSaving] = useState(null);
  const saveTimers = React.useRef({});
  const leadtimeTimers = React.useRef({});

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const productMap = {};
  (products || []).forEach(p => { productMap[p.sku] = p; });

  const items = (innostock || []).map(p => {
    const prod = productMap[p.sku];
    const minLevel = editMin[p.sku] !== undefined ? editMin[p.sku] : (prod?.min_level_innostock ?? 0);
    return { ...p, prod, minLevel, isLow: p.freestock < minLevel };
  }).sort((a, b) => {
    const ratioA = a.minLevel > 0 ? a.freestock / a.minLevel : Infinity;
    const ratioB = b.minLevel > 0 ? b.freestock / b.minLevel : Infinity;
    return ratioA - ratioB;
  });

  const lowItems = items.filter(i => i.isLow);
  const okItems = items.filter(i => !i.isLow);

  const handleSaveMin = async (sku) => {
    const prod = productMap[sku];
    if (!prod) return;
    setSaving(sku);
    await fetch(`${API}/products/${prod.id}/minlevel`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
      body: JSON.stringify({ min_level_innostock: parseInt(editMin[sku]) })
    });
    setEditMin(prev => { const n = { ...prev }; delete n[sku]; return n; });
    if (onProductsRefetch) onProductsRefetch();
    setSaving(null);
  };

  const handleSaveLeadtime = async (sku) => {
    const prod = productMap[sku];
    if (!prod) return;
    const weeks = parseInt(editLeadtime[sku]);
    if (isNaN(weeks) || weeks < 1) return;
    await fetch(`${API}/products/${prod.id}/minlevel`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
      body: JSON.stringify({ leadtime_weeks: weeks })
    });
    setEditLeadtime(prev => { const n = { ...prev }; delete n[sku]; return n; });
    if (onProductsRefetch) onProductsRefetch();
  };

  const renderRow = (item) => {
    const pct = item.minLevel > 0
      ? Math.min(100, Math.round((item.freestock / item.minLevel) * 100))
      : 100;
    return (
      <TableRow key={item.sku} sx={item.isLow ? { backgroundColor: '#fff3f3' } : {}}>
        <TableCell>
          <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
          <Typography variant="caption" color="text.secondary">{item.sku}</Typography>
        </TableCell>
        <TableCell align="center">
          <Chip
            label={item.freestock}
            color={item.freestock === 0 ? 'error' : item.isLow ? 'warning' : 'success'}
            size="small"
          />
        </TableCell>
        <TableCell sx={{ minWidth: 120 }}>
          <LinearProgress
            variant="determinate" value={pct}
            color={pct < 50 ? 'error' : pct < 80 ? 'warning' : 'success'}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" color="text.secondary">{pct}%</Typography>
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              type="number" size="small" value={editMin[item.sku] ?? item.minLevel}
              onChange={e => {
                const val = e.target.value;
                setEditMin(prev => ({ ...prev, [item.sku]: val }));
                // Debounced autosave: 800ms na laatste wijziging
                if (saveTimers.current[item.sku]) clearTimeout(saveTimers.current[item.sku]);
                saveTimers.current[item.sku] = setTimeout(() => {
                  handleSaveMin(item.sku);
                }, 800);
              }}
              inputProps={{ min: 0, style: { width: 50, textAlign: 'center' } }}
              variant="outlined"
            />
            {saving === item.sku && <CircularProgress size={14} />}
          </Box>
        </TableCell>
        <TableCell align="center">
          <TextField
            type="number" size="small"
            value={editLeadtime[item.sku] ?? (item.prod?.leadtime_weeks ?? 2)}
            onChange={e => {
              const val = e.target.value;
              setEditLeadtime(prev => ({ ...prev, [item.sku]: val }));
              if (leadtimeTimers.current[item.sku]) clearTimeout(leadtimeTimers.current[item.sku]);
              leadtimeTimers.current[item.sku] = setTimeout(() => {
                handleSaveLeadtime(item.sku);
              }, 800);
            }}
            inputProps={{ min: 1, max: 26, style: { width: 40, textAlign: 'center' } }}
            variant="outlined"
          />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          InnoStock voorraad {lowItems.length > 0 && (
            <Chip label={`${lowItems.length} onder minimum`} color="error" size="small" sx={{ ml: 1 }} />
          )}
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={refetch} size="small">Vernieuwen</Button>
      </Box>

      {lowItems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {lowItems.length} product(en) onder het minimumniveau bij InnoStock.
        </Alert>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>InnoStock</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Min. niveau</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Levertijd (wkn)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lowItems.map(renderRow)}
            {okItems.map(renderRow)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}


// ─── Tab 5: Te verwerken (Vincent) ───────────────────────────────────────────
function VincentTab({ products }) {
  const [orders, setOrders] = useState([]);
  const [picqerCount, setPicqerCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState({});
  const [done, setDone] = useState({});
  const [skipped, setSkipped] = useState({});
  const [productMap, setProductMap] = useState({});

  // Bouw een map van SKU → product voor koppeling
  useEffect(() => {
    if (products) {
      const map = {};
      products.forEach(p => {
        if (p.sku) map[p.sku.toLowerCase()] = p;
        if (p.barcode) map[p.barcode] = p;
      });
      setProductMap(map);
    }
  }, [products]);

  const checkOrders = async () => {
    setLoading(true);
    setDone({});
    setSkipped({});
    try {
      const res = await fetch(`${API}/vincent-orders`, { headers: { 'x-api-key': APP_API_KEY } });
      const data = await res.json();
      setOrders(data.orders || []);
      setPicqerCount(data.picqer_refs || 0);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const processOrder = async (order) => {
    setProcessing(p => ({ ...p, [order.id]: true }));
    // Koppel items aan inventory_products via SKU
    const items = order.items
      .map(item => {
        const prod = productMap[item.sku?.toLowerCase()] || productMap[item.sku];
        return prod ? { product_id: prod.id, quantity: item.quantity, name: item.name } : null;
      })
      .filter(Boolean);

    if (items.length === 0) {
      alert(`Geen producten gekoppeld voor order ${order.order_number}. Controleer of SKU's overeenkomen.`);
      setProcessing(p => ({ ...p, [order.id]: false }));
      return;
    }

    try {
      const res = await fetch(`${API}/vincent-orders/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
        body: JSON.stringify({ items, order_id: order.id, order_reference: order.reference })
      });
      const data = await res.json();
      const errors = data.results?.filter(r => r.error);
      if (errors?.length > 0) {
        alert(`Fout bij ${errors.length} product(en): ${errors.map(e => e.error).join(', ')}`);
      } else {
        setDone(d => ({ ...d, [order.id]: true }));
        setOrders(prev => prev.filter(o => o.id !== order.id));
      }
    } catch (e) {
      alert('Fout bij verwerken: ' + e.message);
    }
    setProcessing(p => ({ ...p, [order.id]: false }));
  };

  const skipOrder = (orderId) => {
    setSkipped(s => ({ ...s, [orderId]: true }));
    setOrders(prev => prev.filter(o => o.id !== orderId));
  };

  const pendingCount = orders.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          onClick={checkOrders}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ShippingIcon />}
          sx={{ backgroundColor: '#ffcc00', color: '#000', '&:hover': { backgroundColor: '#f5c200' } }}
        >
          {loading ? 'Controleren...' : 'Controleer nieuwe verzendingen'}
        </Button>
        {picqerCount > 0 && (
          <Typography variant="body2" color="text.secondary">
            {picqerCount} orders al in Picqer (gefilterd)
          </Typography>
        )}
      </Box>

      {orders.length === 0 && !loading && picqerCount === 0 && (
        <Alert severity="info">Klik op de knop om orders te controleren.</Alert>
      )}

      {orders.length === 0 && !loading && picqerCount > 0 && (
        <Alert severity="success">Alle verzendingen zijn verwerkt ✓</Alert>
      )}

      {orders.map(order => {
        const uncoupledItems = order.items.filter(item => !productMap[item.sku?.toLowerCase()] && !productMap[item.sku]);
        return (
          <Paper key={order.id} elevation={1} sx={{ p: 2, mb: 2, borderLeft: '4px solid #ffcc00' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Typography fontWeight="bold">
                  {order.source} #{order.order_number}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {order.customer_company && (
                    <span style={{fontWeight: 600, color: '#222', display: 'block'}}>{order.customer_company}</span>
                  )}
                  {order.customer_name && (
                    <span style={{color: '#555', display: 'block'}}>{order.customer_name}</span>
                  )}
                  <span style={{color: '#999'}}>{new Date(order.date).toLocaleDateString('nl-NL')}</span>
                </Typography>
              </Box>
              <Chip label={order.source} size="small" sx={{ backgroundColor: order.source === 'Shopify' ? '#96bf48' : '#7f54b3', color: '#fff' }} />
            </Box>

            <Box sx={{ mb: 2 }}>
              {order.items.map((item, i) => {
                const coupled = productMap[item.sku?.toLowerCase()] || productMap[item.sku];
                return (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2">{item.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={`×${item.quantity}`} size="small" />
                      {!coupled && <Chip label="SKU niet gekoppeld" size="small" color="warning" />}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {uncoupledItems.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                {uncoupledItems.length} product(en) niet automatisch gekoppeld. Handmatig boeken via Transfer.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                variant="contained"
                size="small"
                disabled={processing[order.id]}
                onClick={() => processOrder(order)}
                startIcon={processing[order.id] ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />}
                sx={{ backgroundColor: '#2e7d32', color: '#fff', '&:hover': { backgroundColor: '#1b5e20' } }}
              >
                Afboeken Vincent
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={() => skipOrder(order.id)}
              >
                Overslaan
              </Button>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}


// ─── Bestellen Tab ────────────────────────────────────────────────────────────
function BestellenTab({ products }) {
  const { data: stock, loading: stockLoading } = useApi('/stock');
  const { data: innostock, loading: innLoading, refetch } = useApi('/innostock');

  if (stockLoading || innLoading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const productMap = {};
  (products || []).forEach(p => { productMap[p.sku] = p; });

  const innostockMap = {};
  (innostock || []).forEach(p => { innostockMap[p.sku] = p; });

  const matrix = {};
  (stock || []).forEach(s => {
    const sku = s.inventory_products?.sku;
    const wh = s.inventory_warehouses?.name;
    if (wh === 'InnoStock') return;
    if (!matrix[sku]) matrix[sku] = 0;
    matrix[sku] += s.quantity ?? 0;
  });

  const rows = Object.values(productMap)
    .filter(p => !p.hidden)
    .filter(p => (p.min_level_innostock ?? 0) > 0)
    .map(p => {
      const inn = innostockMap[p.sku];
      const innQty = inn?.freestock ?? 0;
      const extQty = matrix[p.sku] ?? 0;
      const totaal = innQty + extQty;
      const min = p.min_level_innostock;
      const tekort = Math.max(0, min - innQty);
      const status = innQty === 0 ? 'leeg' : innQty < min ? 'laag' : 'ok';
      return { ...p, innQty, extQty, totaal, min, tekort, status };
    })
    .filter(r => r.status !== 'ok')
    .sort((a, b) => a.innQty - b.innQty);

  const handleExportCsv = () => {
    const lines = [
      ['SKU', 'Product', 'InnoStock', 'Extern', 'Totaal', 'Min. niveau', 'Bijbestellen'].join(';'),
      ...rows.map(r => [r.sku, r.name, r.innQty, r.extQty, r.totaal, r.min, r.tekort].join(';'))
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `besteladvies_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight="bold">Besteladvies</Typography>
          {rows.length > 0 && (
            <Chip label={rows.length + ' product' + (rows.length !== 1 ? 'en' : '')} color="warning" size="small" />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {rows.length > 0 && (
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleExportCsv} variant="outlined">CSV</Button>
          )}
          <Button size="small" startIcon={<RefreshIcon />} onClick={refetch}>Vernieuwen</Button>
        </Box>
      </Box>

      {rows.length === 0 ? (
        <Alert severity="success" icon={<CheckIcon />}>
          Alle producten zitten boven het minimumniveau bij InnoStock. Niets te bestellen!
        </Alert>
      ) : (
        <>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {rows.filter(r => r.status === 'leeg').length > 0 && (
              <strong>{rows.filter(r => r.status === 'leeg').length} product(en) volledig leeg bij InnoStock. </strong>
            )}
            {rows.length} product(en) onder het minimumniveau.
          </Alert>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#fff8e1' }}>InnoStock</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Extern</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9' }}>Totaal</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Min.</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: 'error.main' }}>Bijbestellen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.sku} sx={{ backgroundColor: r.status === 'leeg' ? '#ffebee' : '#fff8f0' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {r.status === 'leeg' && <Chip label="LEEG" color="error" size="small" sx={{ fontSize: 10, height: 18 }} />}
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{r.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.sku}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#fffde7' }}>
                      <Chip label={r.innQty} color={r.innQty === 0 ? 'error' : 'warning'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{r.extQty}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#f1f8f1' }}>
                      <Typography variant="body2" fontWeight="bold">{r.totaal}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption" color="text.secondary">{r.min}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={'+' + r.tekort} color="error" size="small" sx={{ fontWeight: 'bold' }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

// ─── Tab 4: Historie ─────────────────────────────────────────────────────────
function HistorieTab() {
  const { data: transfers, loading, refetch } = useApi('/transfers');

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">Transfer historie</Typography>
        <Button startIcon={<RefreshIcon />} onClick={refetch} size="small">Vernieuwen</Button>
      </Box>

      {(!transfers || transfers.length === 0) ? (
        <Alert severity="info">Nog geen transfers geboekt.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Datum</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Van</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Naar</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Aantal</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Opmerking</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(transfers || []).map(t => (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(t.transfer_date).toLocaleDateString('nl-NL', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{t.inventory_products?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.inventory_products?.sku}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={t.from_wh?.name || '—'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={t.to_wh?.name || '—'} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight="bold">{t.quantity}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{t.notes || '—'}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ─── Hoofd component ─────────────────────────────────────────────────────────
export default function Inventory() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const subtabParam = params.get('subtab');
  const initialTab = subtabParam === 'transfer' ? 1 : subtabParam === 'vincent' ? 2 : subtabParam === 'alerts' ? 3 : subtabParam === 'bestellen' ? 4 : subtabParam === 'historie' ? 5 : 0;

  const [tab, setTab] = useState(initialTab);
  const [preselectedProduct, setPreselectedProduct] = useState(null);
  const { data: warehouses } = useApi('/warehouses');
  const { data: products, refetch: refetchProducts } = useApi('/products?include_hidden=1');
  const [syncing, setSyncing] = useState(false);
  const visibleProducts = (products || []).filter(p => !p.hidden);

  const handleTransferClick = (product) => {
    setPreselectedProduct(product);
    setTab(1);
  };

  const handleSync = async () => {
    setSyncing(true);
    await fetch(`${API}/products/sync`, { method: 'POST' });
    await refetchProducts();
    setSyncing(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">Voorraadbeheer</Typography>
        <IconButton
          onClick={handleSync}
          disabled={syncing}
          size="small"
          title="Sync producten (Picqer)"
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1 }}
        >
          {syncing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab icon={<WarehouseIcon />} label="Overzicht" iconPosition="start" sx={{ minWidth: 0, px: 1.5, fontSize: '0.75rem' }} />
        <Tab icon={<TransferIcon />} label="Transfer" iconPosition="start" sx={{ minWidth: 0, px: 1.5, fontSize: '0.75rem' }} />
        <Tab icon={<ShippingIcon />} label="Vincent" iconPosition="start" sx={{ minWidth: 0, px: 1.5, fontSize: '0.75rem' }} />
        <Tab icon={<AlertIcon />} label="Alerts" iconPosition="start" sx={{ minWidth: 0, px: 1.5, fontSize: '0.75rem' }} />
        <Tab icon={<OrderIcon />} label="Bestellen" iconPosition="start" sx={{ minWidth: 0, px: 1.5, fontSize: '0.75rem' }} />
        <Tab icon={<HistoryIcon />} label="Historie" iconPosition="start" sx={{ minWidth: 0, px: 1.5, fontSize: '0.75rem' }} />
      </Tabs>

      {tab === 0 && <OverzichtTab warehouses={warehouses} products={products} onTransferClick={handleTransferClick} onProductsRefetch={refetchProducts} />}
      {tab === 1 && <TransferTab warehouses={warehouses} products={visibleProducts} preselectedProduct={preselectedProduct} />}
      {tab === 2 && <VincentTab products={visibleProducts} />}
      {tab === 3 && <AlertsTab products={visibleProducts} onProductsRefetch={refetchProducts} />}
      {tab === 4 && <BestellenTab products={products} />}
      {tab === 5 && <HistorieTab />}
    </Box>
  );
}
